import React, { useCallback } from 'react';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { emptySelectOption, Option } from '../select';
import ReactSelect, { MenuPlacement, MenuPosition } from 'react-select';

export type SyncSelectType = {
  name: string;
  parentName: string;
  disabled: boolean;
  multi?: boolean;
  clearable?: boolean;
  options: Option[];
  menuPlacement?: MenuPlacement;
  menuPosition?: MenuPosition;
};

const theme = {
  borderRadius: 0,
  colors: {
    primary: '',
    primary75: '',
    primary50: '',
    primary25: '',
    danger: '',
    dangerLight: '',
    neutral0: '',
    neutral5: '',
    neutral10: '',
    neutral20: '',
    neutral30: '',
    neutral40: '',
    neutral50: '',
    neutral60: '',
    neutral70: '',
    neutral80: '',
    neutral90: '',
  },
  spacing: { baseUnit: 0, controlHeight: 0, menuGutter: 0 },
};

export default function SyncSelect({
  name,
  parentName,
  disabled,
  multi,
  clearable,
  options,
  menuPlacement,
  menuPosition,
}: SyncSelectType) {
  const { t } = useTranslation();

  const loading = useCallback(() => t('Loading'), [t]);
  const noOptions = useCallback(() => t('No options'), [t]);

  const selectName = parentName ? `${parentName}.${name}` : name;
  const {
    field: { value, onBlur, onChange },
  } = useController({ name: selectName, shouldUnregister: false });

  const customOnChange = useCallback(
    (event: any) => {
      onBlur();

      let value = null;

      if (Array.isArray(event)) {
        value = event.map((value) => value.value);
      } else if (event !== null && typeof event === 'object') {
        value = event.value;
      }

      onChange(value ?? emptySelectOption.value);
    },
    [onBlur, onChange]
  );

  const findValue = (
    value: string[] | string | any,
    option: Option
  ): boolean => {
    if (option.options) {
      return option.options.filter((o) => findValue(value, o)).length !== 0;
    } else if (Array.isArray(value)) {
      return value.includes(option.value);
    } else if (typeof value === 'object' && value !== null) {
      return option.value === value.value;
    } else {
      return option.value === value;
    }
  };

  const foundValue = options
    .filter((option) => findValue(value, option))
    .flatMap((option) => {
      if (option.options) {
        return option.options.filter((o) => findValue(value, o));
      } else {
        return [option];
      }
    });

  return (
    <div data-name={selectName}>
      <ReactSelect
        name={selectName}
        isMulti={multi}
        isClearable={clearable}
        isDisabled={disabled}
        onBlur={onBlur}
        classNamePrefix={'rs'}
        placeholder={''}
        theme={theme}
        menuPlacement={menuPlacement}
        menuPosition={menuPosition}
        loadingMessage={loading}
        noOptionsMessage={noOptions}
        onChange={customOnChange}
        value={foundValue}
        options={options}
      />
    </div>
  );
}
